import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from "recharts";
import { Gauge, Clock, Coins, TrendingUp, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const DISCIPLINE_REUSE = [
    { discipline: "Mechanical",      reuse: 62 },
    { discipline: "Piping",          reuse: 58 },
    { discipline: "Electrical",      reuse: 51 },
    { discipline: "Instrumentation", reuse: 54 },
    { discipline: "Civil",           reuse: 44 },
    { discipline: "Structural",      reuse: 47 },
    { discipline: "Process",         reuse: 61 },
    { discipline: "HVAC",            reuse: 32 },
];

const PRODUCTIVITY_BY_DISCIPLINE = [
    { discipline: "Mechanical",      productivity: 1.42 },
    { discipline: "Piping",          productivity: 1.38 },
    { discipline: "Electrical",      productivity: 1.31 },
    { discipline: "Instrumentation", productivity: 1.34 },
    { discipline: "Civil",           productivity: 1.22 },
    { discipline: "Structural",      productivity: 1.28 },
    { discipline: "Process",         productivity: 1.44 },
];

export default function EngineeringProductivity() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/edi/summary").then((r) => setData(r.data)).catch(() => toast.error("Failed to load engineering productivity"));
    }, []);
    if (!data) return <div className="kicker text-slate-500">Loading engineering productivity…</div>;

    return (
        <div className="space-y-8" data-testid={EDI.engineeringProductivityRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Engineering Productivity
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        Discipline-level productivity, hours saved, avoided cost and reuse — the measurable
                        engineering impact of the Enterprise Delivery Intelligence Platform™.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">Portfolio · Feb 2026</span>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    ["Engineering Hours Avoided", "218,400", Clock,      "Portfolio, YTD"],
                    ["Equivalent Engineer Years", "121.3",   Coins,      "1,800 hrs / yr"],
                    ["Cost Avoided",              "$32.76M", Coins,      "Blended rate $150/hr"],
                    ["Engineering Productivity",  "1.34×",   Gauge,      "Baseline 1.00"],
                    ["Drawings Reused",           "42,180",  TrendingUp, "Cross-project"],
                    ["Data Sheets Generated",     "642",     TrendingUp, "Auto-populated"],
                    ["Approval Time Reduction",   "-38%",    Clock,      "First-pass 84%"],
                    ["Review Cycle Reduction",    "-31%",    Clock,      "vs. baseline"],
                ].map(([k, v, Icon, s]) => (
                    <div key={k} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                        <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                        <Icon className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <div className="mt-2 kicker text-slate-400 text-[10px]">{k}</div>
                        <div className="mt-2 font-display font-black text-white text-2xl tracking-tighter">{v}</div>
                        <div className="mt-1 text-[11px] text-slate-500 font-body">{s}</div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <div className="glass rounded-xl p-6">
                    <div className="kicker text-slate-400">Reuse % by discipline</div>
                    <h3 className="mt-1 font-display font-black text-white text-xl tracking-tight">
                        Where reuse lands hardest
                    </h3>
                    <div className="h-[340px] mt-3 -ml-3">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={DISCIPLINE_REUSE} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid strokeDasharray="3 6" stroke="rgba(148,163,184,0.12)" horizontal={false} />
                                <XAxis type="number" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <YAxis dataKey="discipline" type="category" stroke="#64748B" width={130} tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }} />
                                <Bar dataKey="reuse" fill="#D4AF37" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="glass rounded-xl p-6">
                    <div className="kicker text-slate-400">Productivity index</div>
                    <h3 className="mt-1 font-display font-black text-white text-xl tracking-tight">
                        Discipline productivity vs baseline
                    </h3>
                    <div className="h-[340px] mt-3">
                        <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={PRODUCTIVITY_BY_DISCIPLINE}>
                                <PolarGrid stroke="rgba(148,163,184,0.18)" />
                                <PolarAngleAxis dataKey="discipline" stroke="#94A3B8" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <PolarRadiusAxis stroke="#475569" tick={{ fontSize: 10 }} angle={30} domain={[0.9, 1.5]} />
                                <Radar dataKey="productivity" stroke="#D4AF37" fill="#D4AF37" fillOpacity={0.3} strokeWidth={2} />
                                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }} />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="glass rounded-xl p-6 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(16,185,129,0.20), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(212,175,55,0.14), transparent 55%)",
                    }}
                />
                <div className="relative">
                    <div className="kicker text-[var(--sgsb-gold)]">Why productivity is up 34%</div>
                    <ul className="mt-3 space-y-2 text-slate-200 font-body text-sm leading-relaxed">
                        <li className="flex gap-3"><span className="text-[var(--sgsb-gold)]">•</span> Engineering Knowledge Reuse™ across 8 active projects — average 62% for Mechanical.</li>
                        <li className="flex gap-3"><span className="text-[var(--sgsb-gold)]">•</span> Automatic Data Sheet Generation™ removed 32,100 engineer hours from the portfolio.</li>
                        <li className="flex gap-3"><span className="text-[var(--sgsb-gold)]">•</span> Intelligent Design Templates™ delivered 83% auto-populated fields across every deliverable.</li>
                        <li className="flex gap-3"><span className="text-[var(--sgsb-gold)]">•</span> OCR Engine™ + Enterprise Search™ cut the average document search time from 38 min to 6.2 s.</li>
                        <li className="flex gap-3"><span className="text-[var(--sgsb-gold)]">•</span> AI Reuse Advisor™ recommendations reduced manual reporting by an estimated 218 hours per project.</li>
                        <li className="flex gap-3"><span className="text-[var(--sgsb-gold)]">•</span> Capture Once. Use Many Times.™ eliminated duplicate reporting across 13 downstream surfaces.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
